import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronRightIcon, MenuIcon, XIcon, type LucideIcon } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";

import brandUrl from "../../../../packages/workerfiles/home/brand.svg";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type WorkspaceNavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  dataNav?: string;
};

export type WorkspaceNavGroup = {
  label: string;
  ariaLabel: string;
  items: WorkspaceNavItem[];
  testId?: string;
};

function WorkspaceNavLink({ item, close }: { item: WorkspaceNavItem; close: () => void }) {
  const Icon = item.icon;
  const { pathname } = useLocation();
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={() => { close(); if (pathname === item.to) window.dispatchEvent(new Event("nodebase-refresh")); }}
      data-nav={item.dataNav}
      className={({ isActive }) => cn("group nb-nav-link", isActive && "nb-nav-link--active shadow-sm")}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      {item.label}
      <ChevronRightIcon className="ml-auto size-3.5 opacity-40" aria-hidden="true" />
    </NavLink>
  );
}

export function WorkspaceShell({
  rootId,
  className,
  mainClassName,
  mainFill = false,
  showSidebar = true,
  reserveNavigationToggle = false,
  compactMobileBrand = false,
  brandHref,
  navigationId = "workspace-navigation",
  navigationLabels = { open: "Open navigation", close: "Close navigation" },
  skipLabel = "Skip to content",
  topNavigation,
  headerActions,
  navGroups,
  sidebarIntro,
  sidebarFooter,
  children,
}: {
  rootId?: string;
  className?: string;
  mainClassName?: string;
  mainFill?: boolean;
  showSidebar?: boolean;
  reserveNavigationToggle?: boolean;
  compactMobileBrand?: boolean;
  brandHref: string;
  navigationId?: string;
  navigationLabels?: { open: string; close: string };
  skipLabel?: string;
  topNavigation?: ReactNode;
  headerActions?: ReactNode;
  navGroups: WorkspaceNavGroup[];
  sidebarIntro?: ReactNode;
  sidebarFooter?: ReactNode;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggle = useRef<HTMLButtonElement>(null);

  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      requestAnimationFrame(() => toggle.current?.focus());
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);

  return (
    <div id={rootId} className={cn("min-h-dvh bg-background", mainFill && "flex h-dvh flex-col overflow-hidden", className)}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-background focus:p-3">
        {skipLabel}
      </a>
      <header className="nb-shell-header sticky top-0 z-30 shrink-0 backdrop-blur">
        <div data-shell-header-inner className="flex min-h-16 flex-wrap items-center gap-3 px-4 py-2 sm:px-6 md:h-16 md:flex-nowrap md:py-0">
          <Link to={brandHref} aria-label="Nodebase" data-shell-brand className="flex shrink-0 items-center gap-2.5 text-xl font-semibold tracking-tight md:w-52">
            <img src={brandUrl} alt="" className="size-8" width="32" height="32" />
            <span className={cn(compactMobileBrand && "hidden sm:inline")}>Nodebase</span>
          </Link>
          {topNavigation}
          <div data-shell-header-actions className="ml-auto flex min-w-0 items-center gap-2">
            {headerActions}
            {showSidebar && <Button
              id="nav-toggle"
              ref={toggle}
              variant="outline"
              size="icon"
              className="md:hidden"
              aria-label={menuOpen ? navigationLabels.close : navigationLabels.open}
              aria-expanded={menuOpen}
              aria-controls={navigationId}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <XIcon /> : <MenuIcon />}
            </Button>}
            {!showSidebar && reserveNavigationToggle && <span className="size-9 shrink-0 md:hidden" aria-hidden="true" />}
          </div>
        </div>
      </header>
      <div className={cn("md:flex", mainFill && "flex min-h-0 flex-1")}>
        {showSidebar && <aside
          id={navigationId}
          className={cn(
            "nb-shell-sidebar shrink-0 border-b bg-muted/20 p-4 md:sticky md:top-16 md:block md:h-[calc(100dvh-64px)] md:overflow-y-auto md:border-b-0",
            !menuOpen && "hidden",
          )}
        >
          {sidebarIntro}
          <div className="space-y-6">
            {navGroups.map((group) => (
              <section key={group.ariaLabel}>
                <p className="mb-3 px-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{group.label}</p>
                <nav className="flex flex-col gap-1" aria-label={group.ariaLabel} data-testid={group.testId}>
                  {group.items.map((item) => <WorkspaceNavLink key={item.to} item={item} close={() => setMenuOpen(false)} />)}
                </nav>
              </section>
            ))}
          </div>
          {sidebarFooter && <div className="mt-6 border-t px-3 pt-4 text-xs leading-relaxed text-muted-foreground">{sidebarFooter}</div>}
        </aside>}
        <main id="main-content" tabIndex={-1} style={mainFill ? { padding: 0, maxWidth: "none" } : undefined} className={cn("nb-shell-main min-w-0 outline-none", mainFill && "flex min-h-0 flex-1 flex-col", mainClassName)}>{children}</main>
      </div>
    </div>
  );
}
